import React, { useState } from 'react';
import { User, Sparkles, Wrench, ChevronRight, ChevronDown, Paperclip } from 'lucide-react';
import type {
  WorkbenchMessage, MessageContentBlock, ToolUseBlock, ToolResultBlock,
} from '../../types';
import { schemaMappingAgentApi } from '../../services/api';
import { renderMarkdown } from '../agents/shared/markdown';
import type { AgentSpec, AgentMessageBubbleProps } from '../agents/shared/types';

/**
 * Schema Mapping Agent bubble: short assistant narration (markdown) +
 * compact tool indicators, AND — anchored to the single
 * create_mapping_run tool call — a rich Mapping Result card rendered
 * from artifacts.mapping. The card reads the LIVE artifact, so it
 * updates in place as polling moves pending → completed → approved
 * (one card, no duplicates per poll). Raw tool_results for the mapping
 * tools are suppressed (the card replaces them). Chat plumbing shared.
 */

// An attached file rides in the message text as a fenced block (so the
// agent receives it) — but the chat must NOT show that wall of JSON.
// Split it off so the bubble renders the typed text + a clean chip.
const ATTACH_RE = /\n*Attached file "([^"]+)":\n```json\n[\s\S]*?```\s*$/;
function splitAttachment(text: string): { body: string; fileName: string | null } {
  const m = text.match(ATTACH_RE);
  if (!m || m.index === undefined) return { body: text, fileName: null };
  return { body: text.slice(0, m.index).trim(), fileName: m[1] };
}

const AttachmentChip: React.FC<{ name: string }> = ({ name }) => (
  <span className="inline-flex items-center gap-1.5 bg-white/15 border border-white/25 rounded-md px-2 py-1 text-[11px]">
    <Paperclip className="w-3 h-3" />
    <span className="font-mono">{name}</span>
  </span>
);

const ToolUseChip: React.FC<{ block: ToolUseBlock }> = ({ block }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="inline-block text-left bg-cargo-surface border border-cargo-border/60 rounded-lg px-3 py-2 text-xs">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-cargo-muted hover:text-cargo-text"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <Wrench className="w-3 h-3 text-cargo-accent" />
        <span className="font-mono text-cargo-accent">{block.name}</span>
        <span className="text-cargo-muted">called</span>
      </button>
      {open && (
        <pre className="mt-2 bg-cargo-panel/60 border border-cargo-border/40 rounded p-2 text-[10px] text-cargo-muted overflow-auto max-h-40">
          {JSON.stringify(block.input ?? {}, null, 2)}
        </pre>
      )}
    </div>
  );
};

const ToolResultChip: React.FC<{ block: ToolResultBlock }> = ({ block }) => {
  const [open, setOpen] = useState(false);
  const text = typeof block.content === 'string'
    ? block.content
    : JSON.stringify(block.content, null, 2);
  return (
    <div className="inline-block text-left bg-cargo-surface border border-cargo-border/60 rounded-lg px-3 py-2 text-xs">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-cargo-muted hover:text-cargo-text"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <span>tool result</span>
      </button>
      {open && (
        <pre className="mt-2 bg-cargo-panel/60 border border-cargo-border/40 rounded p-2 text-[10px] text-cargo-muted overflow-auto max-h-60 whitespace-pre-wrap">
          {text}
        </pre>
      )}
    </div>
  );
};

const Block: React.FC<{ block: MessageContentBlock; isUser: boolean }> = ({ block, isUser }) => {
  if (block.type === 'text') {
    const raw = block.text || '';
    const { body, fileName } = isUser
      ? splitAttachment(raw)
      : { body: raw, fileName: null };
    return (
      <div
        className={`inline-block text-left px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
          isUser
            ? 'bg-cargo-accent text-white'
            : 'bg-cargo-panel border border-cargo-border text-cargo-text'
        }`}
      >
        {(body || !fileName) && renderMarkdown(body, isUser)}
        {fileName && (
          <div className={body ? 'mt-2' : ''}>
            <AttachmentChip name={fileName} />
          </div>
        )}
      </div>
    );
  }
  if (block.type === 'tool_use') return <ToolUseChip block={block as ToolUseBlock} />;
  if (block.type === 'tool_result') return <ToolResultChip block={block as ToolResultBlock} />;
  return null;
};

// ── Rich mapping-result card ────────────────────────────────────────

const pctFromRatio = (r: any) =>
  typeof r === 'number' ? `${(r * 100).toFixed(0)}%` : '—';

const StatusBadge: React.FC<{ status?: string }> = ({ status }) => {
  const map: Record<string, string> = {
    completed: 'bg-cargo-success/15 text-cargo-success border-cargo-success/30',
    running: 'bg-cargo-accent/15 text-cargo-accent border-cargo-accent/30',
    pending: 'bg-cargo-accent/15 text-cargo-accent border-cargo-accent/30',
    failed: 'bg-cargo-error/15 text-cargo-error border-cargo-error/30',
    approved: 'bg-cargo-success/15 text-cargo-success border-cargo-success/30',
    rejected: 'bg-cargo-error/15 text-cargo-error border-cargo-error/30',
    mapped: 'bg-cargo-success/15 text-cargo-success border-cargo-success/30',
    unmapped: 'bg-cargo-panel text-cargo-muted border-cargo-border',
  };
  const cls = map[(status || '').toLowerCase()] || 'bg-cargo-panel text-cargo-muted border-cargo-border';
  return (
    <span className={`px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider ${cls}`}>
      {status || 'unknown'}
    </span>
  );
};

// Build (target_table → target_column → [source mapping]) from the
// source-centric TableMappings, so we can render the TARGET-centric
// table the product uses.
function buildTargetIndex(mappings: any[]): Record<string, Record<string, any[]>> {
  const idx: Record<string, Record<string, any[]>> = {};
  for (const tm of mappings || []) {
    if (!tm || !tm.target_table || tm.mapping_type === 'NO_MAPPING') continue;
    for (const cm of tm.column_mappings || []) {
      if (!cm || !cm.target_column) continue;
      const tt = tm.target_table;
      const tc = cm.target_column;
      if (!idx[tt]) idx[tt] = {};
      if (!idx[tt][tc]) idx[tt][tc] = [];
      idx[tt][tc].push({
        source: `${tm.source_table}.${cm.source_column}`,
        confidence: cm.confidence,
        transformation: cm.transformation,
        notes: cm.notes,
      });
    }
  }
  return idx;
}

// One card per TARGET table: every target column is a row (mapped or
// "— unmapped —"), exactly the product's target-centric layout.
const TargetTableCard: React.FC<{
  tableName: string;
  columns: Array<{ name: string; data_type: string }>;
  srcIndex: Record<string, any[]>;
}> = ({ tableName, columns, srcIndex }) => {
  const mappedCount = columns.filter((c) => (srcIndex[c.name] || []).length > 0).length;
  return (
    <div className="rounded-lg border border-cargo-border bg-cargo-panel/30 px-4 py-3 space-y-2">
      <div className="flex items-center flex-wrap gap-2 text-[13px]">
        <span className="text-cargo-text font-semibold">{tableName}</span>
        <span className="text-cargo-muted text-[11px]">
          {mappedCount}/{columns.length} columns mapped
        </span>
      </div>
      <div className="overflow-hidden rounded-lg border border-cargo-border">
        <table className="w-full table-fixed text-[12px] leading-5">
          <thead className="bg-cargo-panel text-cargo-muted text-[10px] uppercase tracking-wider font-medium">
            <tr>
              <th className="text-left px-3 py-2 align-bottom w-[18%]">Target Column</th>
              <th className="text-left px-3 py-2 align-bottom w-[13%]">Data Type</th>
              <th className="text-left px-3 py-2 align-bottom w-[57%]">Source Mapping(s)</th>
              <th className="text-left px-3 py-2 align-bottom w-[12%]">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-cargo-border">
            {columns.map((col, i) => {
              const srcs = srcIndex[col.name] || [];
              const mapped = srcs.length > 0;
              return (
                <tr key={i} className="hover:bg-cargo-panel transition-colors align-top">
                  <td className="px-3 py-2.5 text-cargo-text font-medium break-words">{col.name}</td>
                  <td className="px-3 py-2.5 text-cargo-muted font-mono text-[11px] break-words">
                    {col.data_type || '—'}
                  </td>
                  <td className="px-3 py-2.5 break-words">
                    {mapped ? (
                      <div className="space-y-2">
                        {srcs.map((sm: any, j: number) => (
                          <div key={j} className="space-y-0.5">
                            <div className="flex items-baseline gap-2">
                              <span className="text-cargo-text font-mono text-[11px] break-all">{sm.source}</span>
                              <span className="text-cargo-accent text-[11px]">{pctFromRatio(sm.confidence)}</span>
                            </div>
                            {sm.notes && (
                              <div className="text-cargo-muted text-[11px] leading-snug">{sm.notes}</div>
                            )}
                            {sm.transformation && (
                              <code className="block bg-cargo-panel/70 border border-cargo-border/40 rounded px-2 py-1 text-cargo-text font-mono text-[10.5px] break-all">
                                {sm.transformation}
                              </code>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-cargo-muted">— unmapped —</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <StatusBadge status={mapped ? 'mapped' : 'unmapped'} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/** The mapping result, anchored to the single create_mapping_run call.
 *  Reads the live artifacts.mapping so it updates in place. Rendered
 *  TARGET-centric: every target column is a row (mapped or unmapped). */
const MappingResult: React.FC<{ artifacts: Record<string, any> }> = ({ artifacts }) => {
  const m = artifacts?.mapping;
  if (!m || !m.run_id) return null;
  const status = (m.status || '').toLowerCase();
  const s = m.summary || {};

  const idx = buildTargetIndex(m.mappings || []);
  // Group target columns by target table. Use the resolved target
  // schema (has ALL columns incl. unmapped); fall back to the mapping
  // index when an older run didn't carry it.
  const groups: Record<string, Array<{ name: string; data_type: string }>> = {};
  if (Array.isArray(m.target_columns) && m.target_columns.length) {
    for (const c of m.target_columns) {
      const tt = c.table_name || '(target)';
      if (!groups[tt]) groups[tt] = [];
      groups[tt].push({ name: c.name, data_type: c.data_type || '' });
    }
  } else {
    for (const tt of Object.keys(idx)) {
      groups[tt] = Object.keys(idx[tt]).map((n) => ({ name: n, data_type: '' }));
    }
  }
  return (
    <div className="mt-2 space-y-3">
      <div className="rounded-lg border border-cargo-border bg-cargo-panel/40 px-4 py-3">
        <div className="flex items-center flex-wrap gap-2 mb-1">
          <span className="text-cargo-text text-[13px] font-semibold">Schema Mapping</span>
          <span className="font-mono text-[11px] text-cargo-muted">{m.run_id}</span>
          <StatusBadge status={m.status} />
          {m.approval_status && m.approval_status !== 'pending' && (
            <StatusBadge status={m.approval_status} />
          )}
        </div>
        <div className="text-[12px] text-cargo-muted">
          {m.source?.schema || '?'} → {m.target?.schema || (m.target?.mode === 'connection' ? '?' : 'provided schema')}
        </div>

        {(status === 'pending' || status === 'running') && (
          <div className="mt-2 text-[12px] text-cargo-accent">Mapping in progress — polling…</div>
        )}

        {status === 'failed' && (
          <div className="mt-2 text-[12px] text-cargo-error">
            Failed{Array.isArray(m.errors) && m.errors.length ? `: ${m.errors.join('; ')}` : '.'}
          </div>
        )}

        {status === 'completed' && (
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[12px]">
            <span className="text-cargo-muted">
              Coverage <span className="text-cargo-text font-medium">{s.coverage_percentage ?? '—'}%</span>
            </span>
            <span className="text-cargo-muted">
              Mapped <span className="text-cargo-text font-medium">{s.mapped_columns ?? '—'}/{s.total_target_columns ?? '—'}</span>
            </span>
            <span className="text-cargo-muted">
              Avg confidence <span className="text-cargo-text font-medium">{s.average_confidence ?? '—'}</span>
            </span>
            <span className="text-cargo-muted">
              Table mappings <span className="text-cargo-text font-medium">{Array.isArray(m.mappings) ? m.mappings.length : 0}</span>
            </span>
          </div>
        )}
      </div>

      {status === 'completed' && Object.keys(groups).map((tt) => (
        <TargetTableCard
          key={tt}
          tableName={tt}
          columns={groups[tt]}
          srcIndex={idx[tt] || {}}
        />
      ))}
    </div>
  );
};

const SchemaMappingMessageBubble: React.FC<AgentMessageBubbleProps> = ({
  message, detailCardToolUseIds,
}) => {
  const isUser = message.role === 'user';
  const content = message.content;

  // Tool-result-only user messages → compact strip; hide the ones whose
  // matching tool_use already has a rich card. Aligned under the content
  // column with a w-8 avatar-gutter spacer (not flush-left).
  if (isUser && Array.isArray(content) && content.every((b: any) => b?.type === 'tool_result')) {
    const visible = (content as ToolResultBlock[]).filter(
      (b) => !detailCardToolUseIds?.has(b.tool_use_id),
    );
    if (visible.length === 0) return null;
    return (
      <div className="flex gap-3 justify-start">
        <div className="w-8 flex-shrink-0" />
        <div className="flex gap-1 flex-wrap">
          {visible.map((b, i) => <ToolResultChip key={i} block={b} />)}
        </div>
      </div>
    );
  }

  // Hide the repeated check_mapping_run poll chips — the result panel
  // already shows live progress. An assistant turn that was ONLY a
  // silent poll (or otherwise empty) then has nothing left → suppress
  // the whole bubble so polling shows no empty avatar rows.
  const blocks = Array.isArray(content)
    ? (content as MessageContentBlock[]).filter(
        (b: any) => !(b?.type === 'tool_use' && b?.name === 'check_mapping_run'),
      )
    : null;
  if (!isUser) {
    if (blocks && blocks.length === 0) return null;
    if (blocks === null && typeof content === 'string' && !content.trim()) return null;
  }

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isUser ? 'bg-cargo-accent/20' : 'bg-cargo-accent text-white'
        }`}
      >
        {isUser ? <User className="w-4 h-4 text-cargo-accent" /> : <Sparkles className="w-4 h-4" />}
      </div>
      <div className={`flex-1 max-w-[80%] ${isUser ? 'text-right' : ''}`}>
        <div className="text-cargo-muted text-[11px] uppercase tracking-wider mb-1">
          {isUser ? 'You' : 'Schema Mapping Agent'}
          {message.at && (
            <span className="ml-2 normal-case tracking-normal opacity-70">
              {new Date(message.at).toLocaleTimeString()}
            </span>
          )}
        </div>
        {typeof content === 'string' ? (
          (() => {
            const { body, fileName } = isUser
              ? splitAttachment(content)
              : { body: content, fileName: null };
            return (
              <div
                className={`inline-block text-left px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  isUser
                    ? 'bg-cargo-accent text-white'
                    : 'bg-cargo-panel border border-cargo-border text-cargo-text'
                }`}
              >
                {(body || !fileName) && body}
                {fileName && (
                  <div className={body ? 'mt-2' : ''}>
                    <AttachmentChip name={fileName} />
                  </div>
                )}
              </div>
            );
          })()
        ) : (
          <div className="space-y-2">
            {(blocks || []).map((block, i) => (
              <div key={i}>
                <Block block={block} isUser={isUser} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const schemaMappingAgentSpec: AgentSpec = {
  title: 'Schema Mapping Agent',
  subtitle:
    'Maps a source schema to a target schema — best target column, transformation, and confidence, per column.',
  heroHeading: 'Map a source schema to a target in chat',
  heroDescription:
    'Connect a source database, then give the target as a connection, '
    + 'pasted JSON, or an attached JSON file. The agent runs the mapping '
    + 'and shows per-column matches with transformations and confidence '
    + 'to review and approve.',
  placeholder: 'Source + target schema — connect, paste, or attach…',

  backRoute: '/schema-mapping-agent',
  sessionRouteBase: '/schema-mapping-agent/sessions',
  queryKeyPrefix: 'schema-mapping-agent',
  streamUrlBase: '/api/schema-mapping-agent',

  api: schemaMappingAgentApi,

  MessageBubble: SchemaMappingMessageBubble,
  // The rich mapping card replaces the raw panels for these tools.
  detailCardTools: ['create_mapping_run', 'check_mapping_run', 'approve_mapping_run'],

  // The result card renders ONCE at the end of the transcript (above
  // the input), next to the final summary — not stranded at the early
  // create_mapping_run call. Reads live artifacts.mapping.
  TrailingPanel: MappingResult,

  // 📎 attach a target/source schema JSON file (read client-side).
  allowFileAttach: true,

  // #1 = one-click DEMO (test creds for the source; target pasted/attached).
  // #2-4 = concrete worked examples for real users (own credentials).
  samplePrompts: [
    {
      label: '▶ DEMO — map a source schema (test credentials)',
      help: 'DEMO_SRC = the SOURCE schema being mapped FROM. "test credentials for the source" = connect the source with the pre-configured demo connection (no connection details entered). "attach the target schema JSON file" = provide the TARGET schema as an uploaded JSON file. Output: each source column mapped to the best target column(s) with a confidence score and a transformation expression.',
      prompt:
        'Map the DEMO_SRC schema to my target. Use test credentials for '
        + 'the source. I will attach the target schema JSON file.',
    },
    {
      label: 'Source (test creds) → target by connection — example',
      help: 'DEMO_SRC = source schema; CUSTOMERS, ORDERS = the source tables to map. Target is a live DB — replace account/warehouse/database/schema/role/user/password with yours; CORE = the target schema it maps INTO.',
      prompt:
        'Use test credentials for the source, map schema DEMO_SRC tables '
        + 'CUSTOMERS and ORDERS. The target is a Snowflake connection — '
        + 'account: ab12345.us-east-1, warehouse: COMPUTE_WH, database: '
        + 'WAREHOUSE, schema: CORE, role: ANALYST, user: jdoe, '
        + 'password: ******** — map into the CORE schema.',
    },
    {
      label: 'Target as pasted JSON — example',
      help: 'Source = demo (DEMO_SRC schema, all tables, test creds). No target DB — you paste the target schema as JSON: a "tables" array, each table with table_name and columns (name, data_type, optional primary_key). It maps the source columns onto those.',
      prompt:
        'Source: use test credentials, schema DEMO_SRC, all tables. '
        + 'Target schema (paste):\n```json\n{\n  "tables": [\n    {\n      '
        + '"table_name": "dim_customer",\n      "columns": [\n        '
        + '{ "name": "customer_key", "data_type": "BIGINT", "primary_key": true },\n        '
        + '{ "name": "full_name", "data_type": "VARCHAR(200)" },\n        '
        + '{ "name": "email", "data_type": "VARCHAR(255)" }\n      ]\n    }\n  ]\n}\n```',
    },
    {
      label: 'Target as an attached JSON file — example',
      help: 'Source = DEMO_SRC schema, all tables, connected via test credentials. Same as the paste example, but the TARGET schema JSON is provided as an uploaded file via the 📎 button instead of pasted inline.',
      prompt:
        'Source: use test credentials, schema DEMO_SRC, all tables. For '
        + 'the target I will attach a schema JSON file — use it as the '
        + 'target dictionary and run the mapping.',
    },
  ],
};
